import { getSingleValueForTasField } from '../../../utils/tuition-assistance/fields.ts';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import type TasProgramTemplateModel from '../../../models/tas-program-template.ts';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import type { GenericTemplateField } from '../../../types/tuition-assistance.ts';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';

interface TuitionAssistanceProgramTemplateReviewNotificationsSignature {
  Args: {
    model: TasProgramTemplateModel;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceProgramTemplateReviewNotificationsComponent extends Component<TuitionAssistanceProgramTemplateReviewNotificationsSignature> {
  @service declare intl: IntlService;
  getSingleValueForTasField = getSingleValueForTasField;

  get reviewFields() {
    return this.args.model?.fields || {};
  }

  get notificationsInfoFields(): { dataKey: TemplateFieldName; label: string }[] {
    return [
      {
        dataKey: 'PRE_APPROVAL_TO_REGISTRATION_MAX_LEAD_TIME',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.lead_time_for_submitting'
        ),
      },
      {
        dataKey: 'PRE_APPROVAL_TO_REGISTRATION_MIN_LEAD_TIME',
        label: this.intl.t('common.tuition_assistance.program_template_review.time_buffer'),
      },
      {
        dataKey: 'REGISTRATION_TO_PRE_APPROVAL_MAX_LAG_TIME',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.time_between_registration_and_pre_approval'
        ),
      },
      {
        dataKey: 'POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.post_course_end_date_evidence_submission'
        ),
      },
      {
        dataKey: 'PAYMENT_SUBMISSION_MAX_DAYS_POST_COMPLETION',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.payment_submission_max_days'
        ),
      },
      {
        dataKey: 'PAYMENT_SUBMISSION_MIN_DAYS_BEFORE_YEAR_END',
        label: this.intl.t(
          'common.program_builder.notifications.payment_submission_min_days_before_year_end'
        ),
      },
      {
        dataKey: 'ESTIMATED_DAYS_TO_PAID',
        label: this.intl.t('common.program_builder.notifications.estimated_days_to_payment'),
      },
      {
        dataKey: 'PAYROLL_EXPECTATION',
        label: this.intl.t('common.tuition_assistance.program_template_review.payroll_expectation'),
      },
      {
        dataKey: 'WEEKDAY_FOR_APPLICATION_REVIEW',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.application_review_schedule'
        ),
      },
      {
        dataKey: 'ELIGIBILITY_WAITING_PERIOD',
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.eligibility_waiting_period'
        ),
      },
    ];
  }

  get computedInfoFields(): {
    dataKey: TemplateFieldName;
    label: string;
    value: GenericTemplateField['values'][0];
  }[] {
    // @ts-expect-error: not sure
    return this.notificationsInfoFields.map((field) => {
      const value = this.getSingleValueForTasField(field.dataKey, this.reviewFields);
      return { ...field, value };
    });
  }

  <template>
    <Section>
      <:header>{{t "common.notifications"}}</:header>
      <:body>
        <section class="grid grid-cols-3 gap-x-8 gap-y-8 pb-5">
          <div class="col-span-3">
            <div class="grid grid-cols-3 gap-4 font-semibold bg-ocean-50 p-4">
              <h3 class="col-span-1">{{t
                  "common.tuition_assistance.program_template_review.field_label"
                }}</h3>
              <h3 class="col-span-2">{{t
                  "common.tuition_assistance.program_template_review.field_value"
                }}</h3>
            </div>
          </div>

          <div class="col-span-3 grid grid-cols-3 p-4 gap-8">
            {{#each this.computedInfoFields as |field|}}
              <p class="col-span-1 col-start-1">{{field.label}}</p>
              {{! @glint-expect-error: Only primitive values and certain DOM objects (see 'ContentValue' in '@glint/template) are usable as top-level template content.}}
              <p class="col-span-1">{{field.value}}</p>
            {{/each}}
          </div>
        </section>
      </:body>
    </Section>
  </template>
}
